import * as React from 'react';
import { ReactComponent as ApiSproketIcon } from 'assets/code-sprocket-icon.svg';
import { ReactComponent as TagIcon } from 'assets/tag-icon.svg';
import { ReactComponent as EnvironmentIcon } from 'assets/environment-icon.svg';
import styled from '@emotion/styled';
import { getAPITypeName } from '../helper-utils';
import { APITypeMap } from 'proto/github.com/solo-io/dev-portal/api/dev-portal/v1beta1/apiproduct_pb';

export interface APIFilter {
  textFilter: string;
  apiSpecFilter: Set<string>;
  environmentFilter: Set<string>;
  tagFilter: Set<string>;
}

export interface APICatalogSearchProps {
  updateFilter: (filter: APIFilter) => void;
  environments: string[];
  tags: string[];
  apiTypes: APITypeMap[keyof APITypeMap][];
  currentFilter: APIFilter;
}
const CheckboxContainer = styled.div`
  input:checked + svg {
    display: block;
  }
`;

export const APICatalogSearch = (props: APICatalogSearchProps) => {
  let changeTextFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.updateFilter({
      textFilter: e.target.value,
      apiSpecFilter: props.currentFilter.apiSpecFilter,
      environmentFilter: props.currentFilter.environmentFilter,
      tagFilter: props.currentFilter.tagFilter
    });
  };

  let changeApiSpecFilter = (apiSpec: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let newFilterSet = new Set(props.currentFilter.apiSpecFilter);
    if (e.target.checked) {
      newFilterSet.add(apiSpec);
    } else {
      newFilterSet.delete(apiSpec);
    }

    props.updateFilter({
      textFilter: props.currentFilter.textFilter,
      apiSpecFilter: newFilterSet,
      environmentFilter: props.currentFilter.environmentFilter,
      tagFilter: props.currentFilter.tagFilter
    });
  };

  let changeEnvironmentFilter = (env: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let newFilterSet = new Set(props.currentFilter.environmentFilter);
    if (e.target.checked) {
      newFilterSet.add(env);
    } else {
      newFilterSet.delete(env);
    }

    props.updateFilter({
      textFilter: props.currentFilter.textFilter,
      apiSpecFilter: props.currentFilter.apiSpecFilter,
      environmentFilter: newFilterSet,
      tagFilter: props.currentFilter.tagFilter
    });
  };

  let changeTagFilter = (tag: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let newFilterSet = new Set(props.currentFilter.tagFilter);
    if (e.target.checked) {
      newFilterSet.add(tag);
    } else {
      newFilterSet.delete(tag);
    }

    props.updateFilter({
      textFilter: props.currentFilter.textFilter,
      apiSpecFilter: props.currentFilter.apiSpecFilter,
      environmentFilter: props.currentFilter.environmentFilter,
      tagFilter: newFilterSet
    });
  };

  return (
    <div className='flex flex-col api-catalog-search'>
      <span className=''>
        <label className='mb-1 text-lg font-medium search-header' htmlFor='catalog-search-input'>
          Filters
        </label>
        <input
          id='catalog-search-input'
          type='search'
          className='w-full px-3 py-2 bg-white border border-gray-500 rounded-lg focus:border-blue-400gloo hover:border-blue-400gloo focus:outline-none search-text-filter focus:ring-4 focus:ring-blue-500gloo focus:ring-opacity-10'
          placeholder='Filter by name or keyword...'
          value={props.currentFilter.textFilter}
          onChange={changeTextFilter}
        />
      </span>
      <div className='mt-4 '>
        {!!props.apiTypes.length && (
          <div className='flex items-center mb-2 search-category-header'>
            <ApiSproketIcon className='w-4 h-4 text-gray-900 fill-current ' title='' />
            <span className='ml-1 text-lg text-gray-700'>API Spec</span>
          </div>
        )}
        <div className='flex flex-col items-start justify-center space-y-2'>
          {props.apiTypes.map(apiSpec => (
            <label
              key={apiSpec}
              className={`flex items-center justify-between w-full pl-3 py-2 bg-white border border-gray-500 rounded-xl cursor-pointer hover:border-blue-400gloo focus-within:border-blue-400gloo focus-within:outline-none search-text-filter focus-within:ring-4 focus-within:ring-blue-500gloo focus-within:ring-opacity-10 ${
                props.currentFilter.apiSpecFilter.has(getAPITypeName(apiSpec)) ? 'bg-blue-100 border-blue-500gloo' : ''
              }`}>
              <span className='text-base text-gray-700'>{getAPITypeName(apiSpec)}</span>
              <CheckboxContainer>
                <div
                  className={`flex items-center justify-center flex-shrink-0 w-6 h-6 mr-2 border rounded cursor-pointer ${
                    props.currentFilter.apiSpecFilter.has(getAPITypeName(apiSpec))
                      ? ' border-blue-500gloo bg-blue-250gloo'
                      : 'border-gray-600 bg-gray-100 focus-within:border-blue-500gloo'
                  } focus-within:border-blue-500gloo`}>
                  <input
                    type='checkbox'
                    className='absolute opacity-0 cursor-pointer'
                    checked={props.currentFilter.apiSpecFilter.has(getAPITypeName(apiSpec))}
                    onChange={changeApiSpecFilter(getAPITypeName(apiSpec))}
                  />
                  <svg className='hidden w-4 h-4 fill-current text-blue-500gloo' viewBox='0 0 20 20' aria-hidden='true'>
                    <path d='M0 11l2-2 5 5L18 3l2 2L7 18z' />
                  </svg>
                </div>
              </CheckboxContainer>
            </label>
          ))}
        </div>
      </div>
      <div className='my-4 '>
        <div className='flex items-center mb-2 search-category-header'>
          <EnvironmentIcon className='w-4 h-4 text-gray-900 fill-current ' title='' />
          <span className='ml-1 text-lg text-gray-700'>Environment</span>
        </div>
        <div className='flex flex-col items-start justify-center space-y-2'>
          {props.environments.map(environment => (
            <label
              key={environment}
              className={`flex items-center justify-between w-full pl-3 py-2 bg-white border border-gray-500 rounded-xl cursor-pointer hover:border-blue-400gloo focus-within:border-blue-400gloo focus-within:outline-none search-text-filter focus-within:ring-4 focus-within:ring-blue-500gloo focus-within:ring-opacity-10 ${
                props.currentFilter.environmentFilter.has(environment) ? 'bg-blue-100 border-blue-500gloo' : ''
              }`}>
              <span className='text-base text-gray-700'>{environment}</span>
              <CheckboxContainer>
                <div
                  className={`flex items-center justify-center flex-shrink-0 w-6 h-6 mr-2 border rounded cursor-pointer ${
                    props.currentFilter.environmentFilter.has(environment)
                      ? ' border-blue-500gloo bg-blue-250gloo'
                      : 'border-gray-600 bg-gray-100 focus-within:border-blue-500gloo'
                  } focus-within:border-blue-500gloo`}>
                  <input
                    type='checkbox'
                    className='absolute opacity-0 cursor-pointer'
                    checked={props.currentFilter.environmentFilter.has(environment)}
                    onChange={changeEnvironmentFilter(environment)}
                  />
                  <svg className='hidden w-4 h-4 fill-current text-blue-500gloo' viewBox='0 0 20 20' aria-hidden='true'>
                    <path d='M0 11l2-2 5 5L18 3l2 2L7 18z' />
                  </svg>
                </div>
              </CheckboxContainer>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className='search-category-header'>
          {!!props.tags.length && (
            <div className='flex items-center mb-2'>
              <TagIcon className='w-4 h-4 text-gray-900 fill-current ' title='' />
              <span className='ml-1 text-lg text-gray-700'>Tags</span>
            </div>
          )}
        </div>
        <div className='flex flex-col items-start space-y-3'>
          {props.tags.map(tag => (
            <label
              key={tag}
              className={`flex items-center justify-between w-full pl-3 py-2 bg-white border border-gray-500 rounded-xl cursor-pointer hover:border-blue-400gloo focus-within:border-blue-400gloo focus-within:outline-none search-text-filter focus-within:ring-4 focus-within:ring-blue-500gloo focus-within:ring-opacity-10 ${
                props.currentFilter.tagFilter.has(tag) ? 'bg-blue-100 border-blue-500gloo' : ''
              }`}>
              <span className='text-base text-gray-700'>{tag}</span>
              <CheckboxContainer>
                <div
                  className={`flex items-center justify-center flex-shrink-0 w-6 h-6 mr-2 border rounded cursor-pointer ${
                    props.currentFilter.tagFilter.has(tag)
                      ? ' border-blue-500gloo bg-blue-250gloo'
                      : 'border-gray-600 bg-gray-100 focus-within:border-blue-500gloo'
                  } focus-within:border-blue-500gloo`}>
                  <input
                    type='checkbox'
                    className='absolute opacity-0 cursor-pointer'
                    checked={props.currentFilter.tagFilter.has(tag)}
                    onChange={changeTagFilter(tag)}
                  />
                  <svg className='hidden w-4 h-4 fill-current text-blue-500gloo' viewBox='0 0 20 20' aria-hidden='true'>
                    <path d='M0 11l2-2 5 5L18 3l2 2L7 18z' />
                  </svg>
                </div>
              </CheckboxContainer>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};
